import * as React from "react";
import { Set } from "immutable";
import { connect, Dispatch } from "react-redux";
import { bindActionCreators } from "redux";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { Button, Dialog, DialogType, mergeStyleSets } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { TargetsList } from "./list";
import { IStore } from "reducers";
import { TARGET_BAG_ID } from "constants/product-collection";
import { DATATEST } from "constants/integration";
import { toggleFinancialTargetGrid, updateSelectedTargetIds } from "actions/financial-target/actions";
import { RenderedComponent, useTargetWizardContext } from "../context/target-wizard-context";
import { AllocationBag } from "helpers/allocation-dimension";
import { TargetsQueueType, updateQueueTypeAction } from "actions/collections/collection-target";
import { UpdateSelectedTargetIdsPayload } from "reducers/collection-target/action-handlers";
import { NewTargetWizardButton } from "./new-target-wizard-button/new-target-wizard-button";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { useWarningThreshold } from "./hooks/useWarningThreshold";
import { getTargetGroupsWithTargets } from "reducers/selectors/collection";
import { TargetGroupWithTargets } from "../../types";
import theme from "styles/theme";
import { setApplied } from "actions/tool-panel/applied/actions";
import { SetApplied } from "actions/tool-panel/applied/types";

export const MANAGE_TARGETS_MODAL_ID = "manage-targets-modal";

interface ReduxProps {
    targetGroupsWithTargets: TargetGroupWithTargets[];
    targetBag: AllocationBag;
    reduxSelectedTargetIds: Set<string>;
    isTargetGridOpen: boolean;
    appliedDimensionsPanel: DimensionsPanelReduxState;
    draftDimensionsPanel: DimensionsPanelReduxState;
}

interface ReduxDispatchProps {
    updateQueueType: (payload: TargetsQueueType) => void;
    updateSelectedTargetIds: (targetIds: any) => Dispatch<UpdateSelectedTargetIdsPayload>;
    toggleTargetGrid: () => Dispatch<void>;
    setApplied: SetApplied;
}

const manageTargetModalStyles = mergeStyleSets({
    newButtonContainer: {
        display: "flex",
        flexDirection: "row",
        borderTop: "1px solid rgb(225, 225, 225)",
        paddingTop: "20px",
    },
    warningContainer: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        padding: "1rem",
        backgroundColor: `${theme.palette.warningAlertBackground} !important`,
    },
});

export const ManageTargetsModalComponent: React.FunctionComponent<ReduxProps & ReduxDispatchProps> = ({
    targetGroupsWithTargets,
    updateSelectedTargetIds,
    toggleTargetGrid,
    reduxSelectedTargetIds,
    isTargetGridOpen,
    appliedDimensionsPanel,
    draftDimensionsPanel,
    setApplied,
}) => {
    const {
        renderedComponent: [_, setRenderedComponent],
        selectedTargetIds: [selectedTargetIds, setSelectedTargetIds],
    } = useTargetWizardContext();
    const warningMessage = useWarningThreshold(appliedDimensionsPanel, targetGroupsWithTargets, selectedTargetIds);

    const toggleTargetGridBasedOnSelectedTargetIds = React.useCallback(() => {
        // If there are selected targets and the grid is closed
        const hasSelectionAndGridIsClosed = selectedTargetIds.size > 0 && !isTargetGridOpen;
        const noSelectionAndGridIsOpen = selectedTargetIds.size === 0 && isTargetGridOpen;
        // If there are no selected targets and the grid is open

        const shouldToggleTargetGrid = hasSelectionAndGridIsClosed || noSelectionAndGridIsOpen;

        shouldToggleTargetGrid && toggleTargetGrid();
    }, [isTargetGridOpen, selectedTargetIds, toggleTargetGrid]);

    const doneButtonPressed = React.useCallback(() => {
        setRenderedComponent(null);
        updateSelectedTargetIds(selectedTargetIds);
        setApplied(draftDimensionsPanel);
        toggleTargetGridBasedOnSelectedTargetIds();
    }, [
        setRenderedComponent,
        updateSelectedTargetIds,
        selectedTargetIds,
        setApplied,
        draftDimensionsPanel,
        toggleTargetGridBasedOnSelectedTargetIds,
    ]);

    React.useEffect(() => {
        setSelectedTargetIds(reduxSelectedTargetIds);
    }, [setSelectedTargetIds, reduxSelectedTargetIds]);

    return (
        <Dialog
            dialogContentProps={{
                type: DialogType.normal,
                title:
                    formatMessage("Manage Targets") + (warningMessage?.length > 0 ? ` (${warningMessage.length})` : ""),
                showCloseButton: true,
            }}
            hidden={false}
            onDismiss={() => setRenderedComponent(RenderedComponent.EMPTY)}
            styles={{
                main: { width: "80vw !important", minWidth: "800px !important", maxWidth: "1200px !important" },
            }}
            data-test={DATATEST.ASSORTMENTS.MANAGE_TARGETS_MODAL}
        >
            <div id={MANAGE_TARGETS_MODAL_ID}>
                {warningMessage && (
                    <div className={manageTargetModalStyles.warningContainer}>
                        <ErrorOutlineIcon
                            style={{ color: `${theme.palette.errorAlertTextColor}`, marginRight: "1rem" }}
                        />
                        <span className="warningText">{warningMessage}</span>
                    </div>
                )}
                <TargetsList />
                <div className={manageTargetModalStyles.newButtonContainer}>
                    <NewTargetWizardButton>{formatMessage("New")}</NewTargetWizardButton>
                    <span style={{ flex: 1 }} />
                    <Button
                        testId={DATATEST.ASSORTMENTS.FOOTER.MANAGE_TARGETS.DONE_BUTTON}
                        primary
                        onClick={doneButtonPressed}
                    >
                        {formatMessage("Done")}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state: IStore) => {
    const targetGroupsWithTargets = getTargetGroupsWithTargets(state);
    const targetBag = state.mutate.snapshots.get(TARGET_BAG_ID);
    const reduxSelectedTargetIds = state.financialTarget.selectedTargetIds;
    const isTargetGridOpen = state.financialTarget.isGridOpen;

    return {
        targetGroupsWithTargets,
        targetBag,
        reduxSelectedTargetIds,
        isTargetGridOpen,
        appliedDimensionsPanel: state.dimensionsPanel.applied,
        draftDimensionsPanel: state.dimensionsPanel.draft,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IStore>) => {
    return {
        updateSelectedTargetIds: (targetIds) => dispatch(updateSelectedTargetIds({ targetIds })),
        updateQueueType: (payload: TargetsQueueType) => dispatch(updateQueueTypeAction(payload)),
        toggleTargetGrid: () => dispatch(toggleFinancialTargetGrid()),
        setApplied: bindActionCreators(setApplied, dispatch),
    };
};

export const ManageTargetsModal = connect<ReduxProps, ReduxDispatchProps, {}>(
    mapStateToProps,
    mapDispatchToProps
)(ManageTargetsModalComponent);
